import { AsyncLocalStorage } from 'async_hooks';
import { Injectable, OnModuleDestroy, OnModuleInit } from '@nestjs/common';
import { Prisma, PrismaClient } from '@prisma/client';

export type PrismaTransactionClient = Prisma.TransactionClient;

/**
 * Mirrors Prisma's own tuple unwrapping for the array form of `$transaction`.
 * Redeclared rather than imported because it lives under Prisma's internal
 * `runtime.Types.Utils` namespace, which is not part of its public surface.
 */
type UnwrapTuple<T extends readonly unknown[]> = {
  [K in keyof T]: T[K] extends Prisma.PrismaPromise<infer U> ? U : T[K];
};

export interface AuditTransactionOptions {
  timeout?: number;
  maxWait?: number;
  /** Runs as the first statement inside the transaction, before any caller work. */
  onOpen?: (tx: PrismaTransactionClient) => Promise<void>;
}

/** Prisma's default of 5s is too tight once a transaction spans a whole request handler. */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * MODULE scope, not instance scope — and that distinction is load-bearing.
 *
 * 22 different Nest modules each list PrismaService in their own `providers`,
 * so the container builds 22 separate instances. An AsyncLocalStorage held as
 * an instance field meant the transaction opened by the audit interceptor (on
 * AuditModule's instance) was invisible to the service performing the write (on
 * CommerceAdminModule's instance), and every audited row still landed as
 * `system`. There is one database and one process, so one shared context is
 * both correct and what the callers actually expect.
 */
const transactionStorage = new AsyncLocalStorage<PrismaTransactionClient>();

@Injectable()
export class PrismaService extends PrismaClient implements OnModuleInit, OnModuleDestroy {
  constructor() {
    super();

    // Returning a Proxy from the constructor means every injected PrismaService
    // is transparently transaction-aware: a service that calls
    // `this.prisma.product.update(...)` inside runInTransaction() has that write
    // routed onto the open transaction without changing a single call site.
    //
    // The trap is kept as cheap as possible — an ALS lookup that returns
    // undefined outside a transaction, which is the overwhelmingly common case.
    return new Proxy(this, {
      get(target, prop, receiver) {
        const tx = transactionStorage.getStore();

        // `$`-prefixed members ($transaction, $queryRaw, $connect…) stay on the
        // real client. Routing $transaction to the tx client would be wrong —
        // it does not exist there — and joining is handled explicitly below.
        if (tx && typeof prop === 'string' && prop.charCodeAt(0) !== 36 /* $ */ && prop in tx) {
          return (tx as unknown as Record<string, unknown>)[prop];
        }

        return Reflect.get(target, prop, receiver);
      },
    });
  }

  async onModuleInit(): Promise<void> {
    await this.$connect();
  }

  async onModuleDestroy(): Promise<void> {
    await this.$disconnect();
  }

  /**
   * Joins the open request transaction instead of opening a second one.
   *
   * `runInTransaction` is re-entrant, but a service calling
   * `this.prisma.$transaction(...)` directly bypassed that: the Proxy above
   * deliberately leaves `$`-prefixed members on the real client, so the nested
   * call opened an INDEPENDENT transaction on a SECOND connection while the
   * request's own transaction was still open.
   *
   * Two ways that bites, both only under load, which is why it does not show up
   * on an idle dev box:
   *   * with a small `connection_limit` the two transactions compete for the
   *     pool and the request times out;
   *   * the inner transaction commits on its own, so its writes survive even
   *     when the outer one fails — leaving exactly the half-created state
   *     reported here (a store with no owner, and a 500).
   *
   * The array form is passed through: those PrismaPromises are already bound to
   * the base client, so re-binding them to the outer transaction is not
   * something this can do safely. None of the current callers use it inside a
   * request-scoped transaction.
   */
  override $transaction<R>(
    fn: (tx: PrismaTransactionClient) => Promise<R>,
    options?: { maxWait?: number; timeout?: number; isolationLevel?: Prisma.TransactionIsolationLevel }
  ): Promise<R>;
  override $transaction<P extends Prisma.PrismaPromise<unknown>[]>(
    arg: [...P],
    options?: { isolationLevel?: Prisma.TransactionIsolationLevel }
  ): Promise<UnwrapTuple<P>>;
  override $transaction(arg: unknown, options?: unknown): Promise<unknown> {
    const active = transactionStorage.getStore();

    if (active && typeof arg === 'function') {
      return (arg as (tx: PrismaTransactionClient) => Promise<unknown>)(active);
    }

    return (super.$transaction as (a: unknown, o?: unknown) => Promise<unknown>)(arg, options);
  }

  /** The open request-scoped transaction, if the caller is inside one. */
  activeTransaction(): PrismaTransactionClient | undefined {
    return transactionStorage.getStore();
  }

  /**
   * Runs `fn` inside a transaction that every `this.prisma.*` model call made
   * within it will join.
   *
   * Re-entrant on purpose: if a transaction is already open for this async
   * context, the callback simply joins it instead of opening a second one.
   * Without that, the services that already call `$transaction` themselves
   * would open a nested transaction on a different connection and deadlock
   * against the outer one.
   */
  async runInTransaction<T>(
    fn: (tx: PrismaTransactionClient) => Promise<T>,
    options: AuditTransactionOptions = {}
  ): Promise<T> {
    const existing = transactionStorage.getStore();
    if (existing) return fn(existing);

    return super.$transaction(
      async (tx) => {
        await options.onOpen?.(tx);
        return transactionStorage.run(tx, () => fn(tx));
      },
      {
        timeout: options.timeout ?? DEFAULT_TIMEOUT_MS,
        maxWait: options.maxWait ?? 5_000,
      }
    );
  }
}
